import axiosInstance from '../config/axios';

export interface StatutoryStageChatMessage {
  id: string;
  partyId: string;
  stageId: string;
  message: string | null;
  createdAt: string | Date;
  createdBy: string | null;
  createdByName: string | null;
  createdByEmail: string | null;
  hasAttachment?: boolean;
  attachmentFilename?: string | null;
  attachmentOriginalName?: string | null;
  attachmentMimetype?: string | null;
  attachmentSize?: number | null;
}

interface ChatMessageApiRow {
  id: string;
  partyId?: string;
  party_id?: string;
  stageId?: string;
  stage_id?: string;
  message: string | null;
  createdAt?: string | Date;
  created_at?: string | Date;
  createdBy?: string | null;
  created_by?: string | null;
  createdByName?: string | null;
  created_by_name?: string | null;
  createdByEmail?: string | null;
  created_by_email?: string | null;
  hasAttachment?: boolean;
  has_attachment?: boolean;
  attachmentFilename?: string | null;
  attachment_filename?: string | null;
  attachmentOriginalName?: string | null;
  attachment_original_name?: string | null;
  attachmentMimetype?: string | null;
  attachment_mimetype?: string | null;
  attachmentSize?: number | null;
  attachment_size?: number | null;
}

interface UnreadCountResponse {
  unreadCount: number;
  lastReadAt: string | null;
}

interface AttachmentDownloadResponse {
  url: string;
  filename: string;
  mimetype: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

class StatutoryStageChatService {
  private baseRoute = '/statutory/chat';

  /**
   * Map API row to frontend format
   */
  private mapChatMessageRow(row: ChatMessageApiRow): StatutoryStageChatMessage {
    return {
      id: row.id,
      partyId: row.partyId || row.party_id || '',
      stageId: row.stageId || row.stage_id || '',
      message: row.message || null,
      createdAt: row.createdAt || row.created_at || new Date().toISOString(),
      createdBy: row.createdBy || row.created_by || null,
      createdByName: row.createdByName || row.created_by_name || null,
      createdByEmail: row.createdByEmail || row.created_by_email || null,
      hasAttachment: row.hasAttachment || row.has_attachment || false,
      attachmentFilename: row.attachmentFilename || row.attachment_filename || null,
      attachmentOriginalName: row.attachmentOriginalName || row.attachment_original_name || null,
      attachmentMimetype: row.attachmentMimetype || row.attachment_mimetype || null,
      attachmentSize: row.attachmentSize || row.attachment_size || null,
    };
  }

  /**
   * List chat messages for a party/stage
   */
  async listChatMessages(
    partyId: string,
    stageId: string,
    options?: { limit?: number }
  ): Promise<StatutoryStageChatMessage[]> {
    if (!partyId || !stageId) {
      return [];
    }

    const config =
      options?.limit && options.limit > 0
        ? { params: { limit: options.limit } }
        : undefined;

    const response = await axiosInstance.get<ApiResponse<ChatMessageApiRow[]>>(
      `${this.baseRoute}/${partyId}/${stageId}/messages`,
      config
    );

    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    return rows.map((row) => this.mapChatMessageRow(row));
  }

  /**
   * Post a new chat message (text only)
   */
  async postChatMessage(
    partyId: string,
    stageId: string,
    message: string
  ): Promise<StatutoryStageChatMessage> {
    if (!partyId || !stageId) {
      throw new Error('Party ID and Stage ID are required');
    }

    const response = await axiosInstance.post<ApiResponse<ChatMessageApiRow>>(
      `${this.baseRoute}/${partyId}/${stageId}/messages`,
      { message }
    );

    return this.mapChatMessageRow(response.data?.data);
  }

  /**
   * Post a chat message with file attachment
   */
  async postChatMessageWithAttachment(
    partyId: string,
    stageId: string,
    message: string | null,
    file: File
  ): Promise<StatutoryStageChatMessage> {
    if (!partyId || !stageId) {
      throw new Error('Party ID and Stage ID are required');
    }

    const formData = new FormData();
    if (message) {
      formData.append('message', message);
    }
    formData.append('file', file);

    const response = await axiosInstance.post<ApiResponse<ChatMessageApiRow>>(
      `${this.baseRoute}/${partyId}/${stageId}/messages/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return this.mapChatMessageRow(response.data?.data);
  }

  /**
   * Get attachment download URL
   */
  async getAttachmentUrl(messageId: string): Promise<AttachmentDownloadResponse> {
    const response = await axiosInstance.get<ApiResponse<AttachmentDownloadResponse>>(
      `${this.baseRoute}/attachment/${messageId}`
    );

    return response.data?.data;
  }

  /**
   * Mark all messages as read for the current user
   */
  async markAsRead(
    partyId: string,
    stageId: string,
    lastMessageId?: string
  ): Promise<void> {
    if (!partyId || !stageId) {
      return;
    }

    await axiosInstance.post(
      `${this.baseRoute}/${partyId}/${stageId}/mark-read`,
      { lastMessageId }
    );
  }

  /**
   * Get unread message count for the current user
   */
  async getUnreadCount(
    partyId: string,
    stageId: string
  ): Promise<UnreadCountResponse> {
    if (!partyId || !stageId) {
      return { unreadCount: 0, lastReadAt: null };
    }

    const response = await axiosInstance.get<ApiResponse<UnreadCountResponse>>(
      `${this.baseRoute}/${partyId}/${stageId}/unread-count`
    );

    return response.data?.data || { unreadCount: 0, lastReadAt: null };
  }
}

export const statutoryStageChatService = new StatutoryStageChatService();
export default statutoryStageChatService;
